import type { ItemIncidentRow, ItemIncidentStatusCategory } from '@/lib/api';

/**
 * アイテム確認依頼の集計（サービス別・担当バイヤー別）と未完了一覧の並べ替え。
 *
 * 数え方の約束:
 * - 未着手・対応中は「いま残っている件数」なので期間で絞らない（古い依頼ほど放置に気付きたい）
 * - 完了は全期間で数えると過去分が積み上がって意味を持たないため、期間内に起票された分だけ数える
 */

/** @graph-connects none */
export type ItemIncidentPeriod = '30d' | '90d' | '365d' | 'all';

/** @graph-connects none */
export const PERIOD_LABEL: Record<ItemIncidentPeriod, string> = {
  '30d': '直近30日',
  '90d': '直近90日',
  '365d': '直近1年',
  all: '全期間',
};

/** @graph-connects none */
const PERIOD_DAYS: Record<ItemIncidentPeriod, number | null> = {
  '30d': 30,
  '90d': 90,
  '365d': 365,
  all: null,
};

/** 担当（確認者）が空の依頼をまとめる表示名 */
/** @graph-connects none */
export const UNASSIGNED = '未割当';

/** @graph-connects none */
export interface StatusCounts {
  notStarted: number;
  inProgress: number;
  done: number;
}

/** @graph-connects none */
export interface GroupCounts extends StatusCounts {
  key: string;
}

/** @graph-connects none */
export interface ItemIncidentSummary {
  totals: StatusCounts;
  byService: GroupCounts[];
  byChecker: GroupCounts[];
}

/** @graph-connects none */
const DAY_MS = 86_400_000;

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 起票日から今日までの経過日数。起票日が読めない依頼は null
 * @graph-connects none
 */
export function elapsedDays(requestedOn: string | null, today: string): number | null {
  if (!requestedOn) return null;
  return Math.round((Date.parse(today) - Date.parse(requestedOn)) / DAY_MS);
}

/** @graph-connects none */
function inPeriod(row: ItemIncidentRow, period: ItemIncidentPeriod, today: string): boolean {
  const days = PERIOD_DAYS[period];
  if (days === null) return true;
  const elapsed = elapsedDays(row.requestedOn, today);
  return elapsed !== null && elapsed < days;
}

/** 集計に数える依頼か。未完了は常に、完了は期間内のものだけ */
/** @graph-connects none */
function counted(row: ItemIncidentRow, period: ItemIncidentPeriod, today: string): boolean {
  return row.statusCategory !== 'done' || inPeriod(row, period, today);
}

/** @graph-connects none */
function emptyCounts(): StatusCounts {
  return { notStarted: 0, inProgress: 0, done: 0 };
}

/** @graph-connects none */
function groupBy(
  rows: readonly ItemIncidentRow[],
  keyOf: (row: ItemIncidentRow) => string,
): GroupCounts[] {
  const map = new Map<string, StatusCounts>();
  for (const row of rows) {
    const key = keyOf(row);
    const counts = map.get(key) ?? emptyCounts();
    counts[row.statusCategory satisfies ItemIncidentStatusCategory] += 1;
    map.set(key, counts);
  }
  return [...map.entries()]
    .map(([key, counts]) => ({ key, ...counts }))
    .sort(
      (a, b) =>
        b.notStarted + b.inProgress - (a.notStarted + a.inProgress) ||
        b.done - a.done ||
        a.key.localeCompare(b.key),
    );
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼を未着手・対応中・完了で数え、サービス別と担当バイヤー別に分ける。未完了は期間に関係なく数え、完了は期間内の起票分だけ数える
 * @graph-connects none
 */
export function summarize(
  rows: readonly ItemIncidentRow[],
  period: ItemIncidentPeriod,
  today: string,
): ItemIncidentSummary {
  const target = rows.filter((row) => counted(row, period, today));
  const totals = emptyCounts();
  for (const row of target) totals[row.statusCategory] += 1;
  return {
    totals,
    byService: groupBy(target, (row) => row.service),
    byChecker: groupBy(target, (row) => row.checker || UNASSIGNED),
  };
}

/** @graph-connects none */
export interface OpenRowFilter {
  service: string | null;
  checker: string | null;
  /** 未着手・対応中のどちらかに絞る。null は両方 */
  status: Exclude<ItemIncidentStatusCategory, 'done'> | null;
  /** No・ブランド・SKU・依頼者・担当・起票区分・確認内容の冒頭のどこかに含まれる語 */
  keyword: string;
}

/** @graph-connects none */
export const EMPTY_FILTER: OpenRowFilter = {
  service: null,
  checker: null,
  status: null,
  keyword: '',
};

/** @graph-connects none */
export type OpenRowSort = 'newest' | 'oldest';

/** @graph-connects none */
export const SORT_LABEL: Record<OpenRowSort, string> = { newest: '新しい順', oldest: '古い順' };

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 絞り込みが1つでも掛かっているか（「条件をクリア」を出すかの判定）
 * @graph-connects none
 */
export function isFiltered(filter: OpenRowFilter): boolean {
  return (
    filter.service !== null ||
    filter.checker !== null ||
    filter.status !== null ||
    filter.keyword.trim() !== ''
  );
}

/** 全角・半角や大文字小文字の違いで検索に漏れないよう揃える */
/** @graph-connects none */
function normalize(text: string): string {
  return text.normalize('NFKC').toLowerCase();
}

/** @graph-connects none */
function matchesKeyword(row: ItemIncidentRow, keyword: string): boolean {
  const words = normalize(keyword).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const haystack = normalize(
    [row.no, row.brand, row.skuId, row.requester, row.checker, row.kind, row.bodyExcerpt].join(' '),
  );
  return words.every((w) => haystack.includes(w));
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 未完了（未着手・対応中）の依頼を、サービス・担当・着手区分・キーワードで絞り、起票日の新しい順（既定）か古い順に並べる
 * @graph-connects none
 */
export function openRows(
  rows: readonly ItemIncidentRow[],
  filter: OpenRowFilter,
  sort: OpenRowSort = 'newest',
): ItemIncidentRow[] {
  const direction = sort === 'newest' ? -1 : 1;
  return rows
    .filter((row) => row.statusCategory !== 'done')
    .filter((row) => filter.service === null || row.service === filter.service)
    .filter((row) => filter.checker === null || (row.checker || UNASSIGNED) === filter.checker)
    .filter((row) => filter.status === null || row.statusCategory === filter.status)
    .filter((row) => matchesKeyword(row, filter.keyword))
    .sort(
      (a, b) =>
        direction *
        ((a.requestedOn ?? '').localeCompare(b.requestedOn ?? '') || a.rowNumber - b.rowNumber),
    );
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 絞り込みの選択肢。未完了の依頼に出てくるサービスと担当を、件数の多い順に返す
 * @graph-connects none
 */
export function openRowOptions(rows: readonly ItemIncidentRow[]): {
  services: string[];
  checkers: string[];
} {
  const open = rows.filter((row) => row.statusCategory !== 'done');
  const ranked = (keys: string[]) => {
    const counts = new Map<string, number>();
    for (const key of keys) counts.set(key, (counts.get(key) ?? 0) + 1);
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([key]) => key);
  };
  return {
    services: ranked(open.map((row) => row.service)),
    checkers: ranked(open.map((row) => row.checker || UNASSIGNED)),
  };
}
